import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  LayoutDashboard,
  BookOpen,
  TrendingUp,
  Users,
  Network,
  CalendarCheck,
  Shield,
  Scale,
  Lock,
  Eye,
  Target,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Database,
  Cpu,
  FileSearch,
  GitBranch,
} from 'lucide-react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export interface GouvernanceAgenticAIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'cadre' | 'maturite' | 'raci' | 'architecture' | 'rituels';

const tabs: { id: TabId; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'vue', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'cadre', label: 'Cadre', icon: BookOpen },
  { id: 'maturite', label: 'Maturité', icon: TrendingUp },
  { id: 'raci', label: 'RACI', icon: Users },
  { id: 'architecture', label: 'Architecture', icon: Network },
  { id: 'rituels', label: 'Gouvernance & rituels', icon: CalendarCheck },
];

const RACI_STYLES: Record<string, string> = {
  R: 'bg-sky-500 text-white',
  A: 'bg-emerald-500 text-white',
  C: 'bg-amber-400 text-slate-900',
  I: 'bg-violet-500 text-white',
};

function RACICell({ type }: { type: string }) {
  return (
    <td className="p-1.5 text-center align-middle">
      <span
        className={`inline-flex min-w-[1.75rem] justify-center rounded-md px-1.5 py-0.5 text-[10px] font-bold ${RACI_STYLES[type] ?? 'bg-slate-600 text-white'}`}
      >
        {type}
      </span>
    </td>
  );
}

function RiskBadge({ level }: { level: 'Faible' | 'Modéré' | 'Élevé' | 'Critique' }) {
  const cls =
    level === 'Faible'
      ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
      : level === 'Modéré'
        ? 'border-amber-400/40 bg-amber-500/15 text-amber-200'
        : level === 'Élevé'
          ? 'border-orange-400/40 bg-orange-500/15 text-orange-200'
          : 'border-rose-400/40 bg-rose-500/15 text-rose-200';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      Risque {level}
    </span>
  );
}

const radarDemo = [
  { axe: 'Gouvernance', cible: 5, actuel: 2.5, fullMark: 5 },
  { axe: 'Identité & accès', cible: 5, actuel: 2, fullMark: 5 },
  { axe: 'Sécurité', cible: 5, actuel: 3, fullMark: 5 },
  { axe: 'Données & RAG', cible: 5, actuel: 2, fullMark: 5 },
  { axe: 'Traçabilité', cible: 5, actuel: 2.5, fullMark: 5 },
  { axe: 'Orchestration', cible: 5, actuel: 2, fullMark: 5 },
  { axe: 'Adoption org.', cible: 5, actuel: 2, fullMark: 5 },
  { axe: 'Supervision humaine', cible: 5, actuel: 3, fullMark: 5 },
];

const raciRoles = [
  'Direction / Sponsor',
  'Owner agent',
  'Resp. sécurité',
  'Resp. données',
  'Resp. plateforme TI',
  'Resp. gouvernance',
  'Utilisateur métier',
  'Audit / conformité',
  'Resp. arch. IA',
];

/** Lignes : activité puis lettres RACI par rôle (ordre raciRoles) */
const raciMatrix: { activity: string; cells: string[] }[] = [
  { activity: "Création d'un agent", cells: ['A', 'R', 'C', 'C', 'C', 'C', 'C', 'I', 'C'] },
  { activity: "Approbation niveau d'autonomie", cells: ['A', 'C', 'C', 'I', 'I', 'R', 'I', 'I', 'C'] },
  { activity: 'Attribution des permissions', cells: ['I', 'C', 'A', 'C', 'R', 'C', 'I', 'C', 'C'] },
  { activity: 'Accès aux données', cells: ['I', 'C', 'C', 'A', 'R', 'C', 'I', 'C', 'I'] },
  { activity: "Intégration d'outils", cells: ['I', 'R', 'C', 'I', 'A', 'C', 'C', 'I', 'R'] },
  { activity: "Validation des cas d'usage", cells: ['A', 'R', 'C', 'C', 'I', 'C', 'R', 'I', 'C'] },
  { activity: 'Revue des journaux', cells: ['I', 'C', 'R', 'C', 'C', 'A', 'I', 'R', 'I'] },
  { activity: 'Revue de performance', cells: ['C', 'R', 'I', 'I', 'C', 'A', 'C', 'I', 'C'] },
  { activity: 'Revue des incidents', cells: ['A', 'R', 'R', 'C', 'C', 'A', 'I', 'R', 'C'] },
  { activity: 'Désactivation / retrait', cells: ['A', 'R', 'C', 'C', 'R', 'A', 'I', 'C', 'I'] },
  { activity: 'Audit périodique', cells: ['A', 'C', 'C', 'C', 'C', 'C', 'I', 'R', 'C'] },
  { activity: "Évolution de l'architecture", cells: ['A', 'C', 'C', 'C', 'R', 'R', 'C', 'C', 'R'] },
];

export default function GouvernanceAgenticAIModal({ isOpen, onClose }: GouvernanceAgenticAIModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'vue':
        return (
          <div className="space-y-6">
            <div className="rounded-3xl border border-indigo-400/20 bg-white/5 p-6">
              <p className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-2">Positionnement</p>
              <p className="text-slate-200 leading-7 text-base md:text-lg">
                Ce cadre vise à encadrer les <strong className="text-white">agents IA capables d&apos;agir</strong> : accès
                contrôlé, autonomie déclarée, traçabilité complète et pilotage humain explicite — pour une adoption
                progressive sans perte de maîtrise en PME/ETI exigeantes.
              </p>
            </div>

            <div className="rounded-3xl border border-emerald-400/20 bg-emerald-500/10 p-6">
              <p className="text-sm font-semibold uppercase tracking-wider text-emerald-200 mb-3">
                Fondations de référence
              </p>
              <p className="text-slate-200 leading-7 text-sm md:text-base mb-4">
                La gouvernance agentique proposée n&apos;est pas une approche maison isolée : elle s&apos;appuie sur des cadres
                reconnus et les adapte aux réalités des agents IA capables d&apos;utiliser des données, des outils et des
                workflows.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  {
                    t: 'NIST AI RMF 1.0',
                    d: 'Socle principal pour gouverner, cartographier, mesurer et gérer les risques liés aux systèmes IA.',
                  },
                  {
                    t: 'NIST AI 600-1 — Generative AI Profile',
                    d: 'Complément pour les risques propres à l’IA générative : hallucinations, contenu, données, abus et transparence.',
                  },
                  {
                    t: 'ISO/IEC 42001',
                    d: 'Référence de système de management IA pour structurer politiques, responsabilités et amélioration continue.',
                  },
                  {
                    t: 'OWASP LLM / Agentic Security',
                    d: 'Repères pratiques pour les risques d’outils, de permissions, de prompt injection et de chaînes agentiques.',
                  },
                ].map((ref) => (
                  <div key={ref.t} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                    <p className="font-bold text-emerald-100 mb-1">{ref.t}</p>
                    <p className="text-xs text-slate-200 leading-5">{ref.d}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-300" />
                Esprit & objectifs macro
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  {
                    t: 'Human-in-control',
                    d: "L'humain conserve l'autorité sur les périmètres sensibles, les seuils d'autonomie et les décisions à impact majeur.",
                  },
                  {
                    t: 'Deny-by-default',
                    d: 'Aucun accès ni outil n\'est accordé par présomption ; chaque capacité est explicitement autorisée et limitée.',
                  },
                  {
                    t: 'Security-by-design',
                    d: 'La sécurité et la confidentialité sont fixées avant l\'élargissement des capacités, pas après les incidents.',
                  },
                  {
                    t: 'Traçabilité & auditabilité',
                    d: 'Décisions, requêtes, actions et intégrations sont journalisées de façon exploitable pour revue et conformité.',
                  },
                  {
                    t: 'Autonomie progressive',
                    d: "Les niveaux d'action s'élargissent seulement après preuve de maîtrise, métriques stables et revues formelles.",
                  },
                  {
                    t: 'Cloisonnement',
                    d: 'Identité, données, outils et corpus RAG sont segmentés : un agent ne « voit » que ce que son mandat autorise.',
                  },
                  {
                    t: 'Alignement affaires',
                    d: 'Chaque agent est rattaché à un cas d\'usage approuvé, un sponsor et des indicateurs de valeur / risque.',
                  },
                ].map((p) => (
                  <div
                    key={p.t}
                    className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 text-sm text-slate-200 leading-6"
                  >
                    <p className="font-bold text-indigo-200 mb-1">{p.t}</p>
                    <p>{p.d}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-5">
              <h3 className="text-lg font-bold text-cyan-200 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Pourquoi cette gouvernance existe
              </h3>
              <p className="text-slate-200 leading-7 text-sm md:text-base mb-4">
                Les agents agentiques ne se limitent pas à du texte : ils peuvent{' '}
                <strong className="text-white">lire des sources</strong>,{' '}
                <strong className="text-white">enchaîner des étapes</strong>,{' '}
                <strong className="text-white">appeler des outils</strong> et{' '}
                <strong className="text-white">interagir avec des systèmes</strong>. Sans cadre, la surface d&apos;erreur,
                de fuite d&apos;information et de décision non assumée augmente vite.
              </p>
              <p className="text-sm mb-2 font-semibold text-white">La gouvernance doit donc couvrir :</p>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-200">
                {[
                  'Identité et imputabilité de chaque agent',
                  'Droits, rôles et périmètres techniques',
                  "Niveau d'autonomie déclaré et supervisé",
                  'Qualité et sélection des données / RAG',
                  'Mécanismes de supervision, d’escalade et d’arrêt',
                  'Responsabilité claire côté organisation (owner, sécurité, données)',
                ].map((x) => (
                  <li key={x} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-cyan-300 mt-0.5" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold text-white mb-3">Valeur apportée</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    icon: Shield,
                    title: 'Réduction du risque',
                    body: 'Surface d’attaque et décisions opaques réduites par deny-by-default, moindre privilège et journaux exploitables.',
                  },
                  {
                    icon: Scale,
                    title: "Encadrement de l'autonomie",
                    body: "Autonomie traitée comme un levier gradué : niveaux explicites, revues et critères d'élargissement.",
                  },
                  {
                    icon: Cpu,
                    title: 'Industrialisation',
                    body: 'Patrons d’identité, de RAG, d’outils et de supervision réutilisables entre équipes et cas d’usage.',
                  },
                  {
                    icon: Target,
                    title: 'Confiance organisationnelle',
                    body: 'Direction, TI, sécurité et métier partagent le même langage : qui décide, qui opère, ce qui est tracé.',
                  },
                ].map((c) => (
                  <div
                    key={c.title}
                    className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-4 flex flex-col gap-2"
                  >
                    <c.icon className="w-8 h-8 text-indigo-200" />
                    <p className="font-bold text-white">{c.title}</p>
                    <p className="text-xs text-slate-200 leading-5">{c.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'cadre':
        return (
          <div className="space-y-8">
            <section>
              <h3 className="text-lg font-bold text-white mb-4">Typologie des agents</h3>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[
                  {
                    name: "Agent d'analyse",
                    role: 'Synthèse, corrélation et signaux à partir de sources autorisées, sans action sur les systèmes.',
                    risk: 'Faible' as const,
                    examples: 'Résumé de dossiers, veille structurée, scoring explicatif.',
                    supervision: 'Revue ponctuelle des sorties ; pas d’accès en écriture.',
                  },
                  {
                    name: "Agent d'assistance",
                    role: 'Copilote : brouillons, formulaires, propositions — l’humain déclenche les actions sensibles.',
                    risk: 'Faible' as const,
                    examples: 'Préparation de réponses, checklists, gabarits conformes.',
                    supervision: 'Validation humaine avant envoi ou enregistrement.',
                  },
                  {
                    name: "Agent d'exécution",
                    role: 'Enchaîne des actions via outils (tickets, API) dans un périmètre strictement autorisé.',
                    risk: 'Modéré' as const,
                    examples: 'Mise à jour d’états, classement, déclenchement de workflows approuvés.',
                    supervision: 'File d’attente, garde-fous techniques et revue des écarts.',
                  },
                  {
                    name: 'Agent décisionnel assisté',
                    role: 'Produit des recommandations ordonnées ; la décision formelle reste humaine et documentée.',
                    risk: 'Modéré' as const,
                    examples: 'Priorisation, scénarios, arbitrages préparés pour un comité.',
                    supervision: 'Signature métier / contrôle qualité sur la décision finale.',
                  },
                  {
                    name: 'Agent autonome supervisé',
                    role: 'Opère en flux avec déclencheurs et limites ; escalade obligatoire hors plage.',
                    risk: 'Élevé' as const,
                    examples: 'Orchestration 24/7 dans un domaine borné avec playbooks.',
                    supervision: 'Supervision active, SLAs, kill switch et revues fréquentes.',
                  },
                  {
                    name: 'Agent orchestrateur',
                    role: 'Coordonne des agents spécialisés ; impose ordre, contrats d’interface et agrégation des journaux.',
                    risk: 'Critique' as const,
                    examples: 'Chaîne multi-étapes client / interne avec sous-agents limités.',
                    supervision: 'Gouvernance centrale, registre des agents, audits renforcés.',
                  },
                ].map((a) => (
                  <div key={a.name} className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="font-bold text-indigo-200">{a.name}</h4>
                      <RiskBadge level={a.risk} />
                    </div>
                    <p className="text-sm text-slate-200 leading-6">{a.role}</p>
                    <p className="text-xs text-slate-400">
                      <span className="text-slate-500 font-semibold">Exemples : </span>
                      {a.examples}
                    </p>
                    <p className="text-xs text-cyan-200/90 border-t border-white/10 pt-2">
                      <span className="font-semibold text-white">Supervision : </span>
                      {a.supervision}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-white mb-4">Niveaux d&apos;autonomie</h3>
              <div className="space-y-3">
                {[
                  {
                    n: 'Niveau 0 — Lecture / observation',
                    desc: "L'agent consulte des sources autorisées et produit de l'information ; aucune action sur systèmes.",
                    ex: 'Veille, extraction, Q&R sur corpus approuvé en lecture seule.',
                    ctrl: 'Contrôles d’accès en lecture, journaux de consultation, DLP si requis.',
                    acc: 'Très largement acceptable ; point de départ par défaut pour la plupart des PME.',
                  },
                  {
                    n: 'Niveau 1 — Suggestion',
                    desc: 'Propositions structurées ; aucune mutation de données sans action humaine explicite.',
                    ex: 'Brouillon de courriel, proposition de tâche, recommandation de classement.',
                    ctrl: 'Validation humaine obligatoire pour « commit » ; boutons d’action désactivés côté agent.',
                    acc: 'Standard pour l’adoption généralisée à faible risque.',
                  },
                  {
                    n: 'Niveau 2 — Action encadrée',
                    desc: 'Actions techniques prédéfinies, limitées et réversibles dans un périmètre contractuel.',
                    ex: 'Mise à jour d’un champ, étiquetage, routage vers une file approuvée.',
                    ctrl: 'Allow-list d’outils, quotas, fenêtres horaires, validation pair pour certains flux.',
                    acc: 'Acceptable si owner nommé et journaux complets ; revue mensuelle minimale.',
                  },
                  {
                    n: 'Niveau 3 — Action supervisée avec escalade',
                    desc: "L'agent enchaîne des étapes ; toute anomalie, incertitude ou seuil déclenche un arrêt ou une montée.",
                    ex: 'Traitement de dossiers avec file active et escalade vers un humain prioritaire.',
                    ctrl: 'Détecteurs de dérive, seuils métier, file de reprise, obligation de preuve de décision.',
                    acc: 'Réservé aux domaines maîtrisés ; double contrôle sécurité / données.',
                  },
                  {
                    n: 'Niveau 4 — Orchestration supervisée',
                    desc: 'Coordination multi-agents / multi-outils avec contrat de service, registre et supervision 24/7 ou équivalent.',
                    ex: 'Chaîne complète d’onboarding, triage complexe, opérations transverses encadrées.',
                    ctrl: 'Registre des agents, corrélation des journaux, exercices d’arrêt, revue trimestrielle élargie.',
                    acc: 'Niveau « excellence » ; exige maturité TI et gouvernance matures.',
                  },
                ].map((row) => (
                  <div
                    key={row.n}
                    className="rounded-2xl border border-indigo-400/15 bg-slate-900/70 p-4 grid lg:grid-cols-2 gap-3 text-sm"
                  >
                    <div>
                      <p className="font-bold text-white mb-1">{row.n}</p>
                      <p className="text-slate-200 leading-6">{row.desc}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        <span className="text-indigo-300 font-semibold">Exemple : </span>
                        {row.ex}
                      </p>
                    </div>
                    <div className="space-y-2 text-xs text-slate-200">
                      <p>
                        <span className="font-semibold text-cyan-200">Mécanisme de contrôle : </span>
                        {row.ctrl}
                      </p>
                      <p>
                        <span className="font-semibold text-emerald-200">Acceptabilité : </span>
                        {row.acc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-white mb-4">Piliers du cadre</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    title: 'Identité & authentification',
                    def: 'Chaque agent possède une identité de service distincte, traçable et non partagée avec les humains.',
                    obj: 'Imputabilité et révocation simple.',
                    risk: 'Comptes génériques et responsabilité floue.',
                  },
                  {
                    title: 'Permissions & périmètre',
                    def: "Moindre privilège matérialisé : rôles, scopes et politiques d'usage par cas d'usage.",
                    obj: 'Réduction des abus et de l’élargissement tacite des droits.',
                    risk: 'Sur-privilèges et accès « au besoin » permanent.',
                  },
                  {
                    title: 'Données & RAG',
                    def: 'Corpus classifiés, indexation sélective, contrôle des sources et limitation du contexte.',
                    obj: 'Confidentialité et qualité des réponses.',
                    risk: 'Fuite par retrieval, empoisonnement ou mélange de sensibilités.',
                  },
                  {
                    title: 'Outils & actions',
                    def: 'Connecteurs approuvés, contrats d’interface, garde-fous et modes « dry-run » lorsque pertinent.',
                    obj: 'Actions prévisibles et réversibles.',
                    risk: 'Chaînes d’actions irréversibles non comprises.',
                  },
                  {
                    title: 'Supervision humaine',
                    def: 'Points de contrôle, files d’escalade et critères de reprise après incident.',
                    obj: 'Maintien du contrôle là où le risque métier l’exige.',
                    risk: 'Automatisation « silencieuse » sans file visible.',
                  },
                  {
                    title: 'Journalisation & audit',
                    def: 'Traces corrélées : prompts sensibles, appels outils, décisions et identités impliquées.',
                    obj: 'Revues, enquêtes et démonstration de conformité.',
                    risk: 'Opacité et incapacité à reconstruire un incident.',
                  },
                  {
                    title: 'Gouvernance continue',
                    def: 'Rituels, indicateurs et arbitrages périodiques sur autonomie, accès et architecture.',
                    obj: 'Le cadre vit au-delà du go-live.',
                    risk: 'Dette de gouvernance et dérive des configurations.',
                  },
                ].map((p) => (
                  <div key={p.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <h4 className="font-bold text-indigo-200 text-sm mb-2">{p.title}</h4>
                    <p className="text-xs text-slate-200 leading-5 mb-2">{p.def}</p>
                    <p className="text-[11px] text-emerald-200/90">
                      <span className="text-white font-semibold">Objectif : </span>
                      {p.obj}
                    </p>
                    <p className="text-[11px] text-rose-200/90 mt-1">
                      <span className="text-white font-semibold">Risque adressé : </span>
                      {p.risk}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-5">
              <h3 className="text-lg font-bold text-amber-100 mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Principes directeurs (normatifs)
              </h3>
              <ul className="space-y-2 text-sm text-slate-100 leading-6">
                {[
                  "Un agent ne reçoit jamais plus de droits que nécessaire — et jamais sans justification documentée.",
                  "Un agent ne prend pas seul une décision critique non traçable ; la chaîne de preuve est exigible.",
                  'Tout accès doit être justifiable, limité dans le temps ou le périmètre, et journalisé.',
                  "Toute autonomie doit être explicitement déclarée, revue et, si besoin, rétrogradée.",
                  'Tout agent a un owner unique : responsabilité claire, sans double commandement implicite.',
                  'Tout flux sensible (données, outils, décisions) est gouverné : pas de raccourcis « pour aller vite ».',
                ].map((line) => (
                  <li key={line} className="flex gap-2">
                    <ArrowRight className="w-4 h-4 shrink-0 text-amber-300 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        );

      case 'maturite':
        return (
          <div className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 min-h-[320px]">
                <h3 className="text-lg font-bold text-white mb-1">Profil-type de démonstration</h3>
                <p className="text-xs text-slate-400 mb-3">
                  Lecture sur 5 : organisation en transition vers un niveau « encadré » (pilotes stables, cadre partagé).
                  Courbe « cible » à 5 pour l’alignement long terme.
                </p>
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarDemo} cx="50%" cy="50%" outerRadius="75%">
                      <PolarGrid stroke="rgba(148,163,184,0.3)" />
                      <PolarAngleAxis dataKey="axe" tick={{ fill: '#cbd5e1', fontSize: 10 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                      <Radar
                        name="État actuel (ex.)"
                        dataKey="actuel"
                        stroke="#818cf8"
                        fill="#818cf8"
                        fillOpacity={0.35}
                      />
                      <Radar name="Cible long terme" dataKey="cible" stroke="#34d399" fill="#34d399" fillOpacity={0.12} />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#e2e8f0' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-5">
                <h3 className="text-lg font-bold text-emerald-100 mb-2 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Lecture exécutive
                </h3>
                <p className="text-sm text-slate-100 leading-7">
                  Une organisation mature en agentique n&apos;est pas celle qui déploie le plus d&apos;agents, mais celle qui
                  maîtrise le <strong className="text-white">cadre</strong>, les{' '}
                  <strong className="text-white">accès</strong>, la{' '}
                  <strong className="text-white">traçabilité</strong> et l&apos;
                  <strong className="text-white">alignement avec la prise de décision humaine</strong>. Le nombre
                  d&apos;agents n&apos;est un indicateur de succès que si chacun est proprement limité, observé et tenu par un
                  owner.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-white mb-4">Échelle de maturité 1 → 5</h3>
              <div className="space-y-3">
                {[
                  {
                    lvl: 'Niveau 1 — Expérimental',
                    fait: 'POC isolés, peu de contrôles standardisés, journaux fragmentés.',
                    manque: 'Owner systématique, politique d’accès unifiée, corrélation des traces.',
                    risque: 'Shadow agents et démonstrations qui deviennent de facto production.',
                    next: 'Formaliser registre minimal, niveau 0–1 d’autonomie uniquement, interdiction d’outils non approuvés.',
                  },
                  {
                    lvl: 'Niveau 2 — Assisté',
                    fait: 'Agents majoritairement en suggestion ; premières politiques TI / sécurité.',
                    manque: 'RAG gouverné, classifications de données appliquées aux corpus.',
                    risque: 'Écart entre « policy » et pratique réelle sur les connecteurs.',
                    next: 'Mettre en place revues mensuelles et indicateurs de couverture des permissions.',
                  },
                  {
                    lvl: 'Niveau 3 — Encadré',
                    fait: 'Cadre RACI publié, autonomie niveau 2 maîtrisée sur cas d’usage sélectionnés.',
                    manque: 'Orchestrations multi-agents avec contrats et corrélation des journaux.',
                    risque: 'Élargissement trop rapide du périmètre sans preuve de stabilité.',
                    next: 'Exercices d’escalade / kill switch et revue trimestrielle des sources RAG.',
                  },
                  {
                    lvl: 'Niveau 4 — Supervisé',
                    fait: 'Supervision active, tableaux de bord, intégration sécurité et données dans les revues.',
                    manque: 'Automatisation des contrôles et benchmarking interne / externe.',
                    risque: 'Fatigue de gouvernance si les rituels ne sont pas soutenus par des métriques.',
                    next: 'Industrialiser les modèles d’identité, de RAG et de déploiement.',
                  },
                  {
                    lvl: 'Niveau 5 — Orchestré',
                    fait: 'Portefeuille d’agents gouverné comme un produit : architecture, risques et valeur pilotés.',
                    manque: 'Amélioration continue externe (écosystème, modèles) sans dérive.',
                    risque: 'Complaisance — le niveau 5 exige la même vigilance qu’au niveau 3.',
                    next: 'Audits indépendants périodiques et intégration explicite des évolutions réglementaires.',
                  },
                ].map((m) => (
                  <div key={m.lvl} className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 text-sm">
                    <p className="font-bold text-indigo-200 mb-2">{m.lvl}</p>
                    <div className="grid md:grid-cols-2 gap-3 text-slate-200">
                      <div>
                        <p className="text-xs font-semibold text-white mb-1">Ce que l’organisation fait</p>
                        <p className="leading-6">{m.fait}</p>
                        <p className="text-xs font-semibold text-white mt-2 mb-1">Ce qui manque</p>
                        <p className="leading-6 text-slate-300">{m.manque}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-rose-200 mb-1">Risque principal</p>
                        <p className="leading-6">{m.risque}</p>
                        <p className="text-xs font-semibold text-emerald-200 mt-2 mb-1">Prochaine étape logique</p>
                        <p className="leading-6">{m.next}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'raci':
        return (
          <div className="space-y-6">
            <p className="text-sm text-slate-300 leading-7">
              Matrice indicative pour PME/ETI : les lettres doivent être adaptées à votre réalité (noms de fonctions,
              délégations, délégation TI). L’important est l&apos;
              <strong className="text-white">explicite</strong> : qui est accountable, qui exécute, qui est consulté, qui est
              informé.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-slate-900/50">
              <table className="w-full text-left text-[11px] md:text-xs min-w-[920px]">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-900/80">
                    <th className="p-2 font-bold text-indigo-200 sticky left-0 bg-slate-900/95 z-10 min-w-[180px]">
                      Activité
                    </th>
                    {raciRoles.map((r) => (
                      <th key={r} className="p-2 font-semibold text-slate-300 text-center whitespace-nowrap max-w-[88px]">
                        {r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {raciMatrix.map((row) => (
                    <tr key={row.activity} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-2 text-slate-200 font-medium sticky left-0 bg-slate-950/90 z-10">{row.activity}</td>
                      {row.cells.map((c, i) => (
                        <RACICell key={`${row.activity}-${raciRoles[i]}`} type={c} />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-5 space-y-4 text-sm text-slate-200 leading-7">
              <h3 className="text-lg font-bold text-white">Ce qu&apos;un owner d&apos;agent doit assumer</h3>
              <p>
                L&apos;owner porte la <strong className="text-white">vie utile</strong> de l&apos;agent : périmètre métier,
                niveau d&apos;autonomie, cohérence avec la politique de sécurité et les données, suivi des incidents et
                décisions d&apos;élargissement ou de retrait. Ce n&apos;est pas un rôle décoratif : c&apos;est la fonction
                qui répond « pourquoi cet agent existe » et « dans quelles limites il demeure acceptable ».
              </p>
              <p>
                <strong className="text-white">Un agent doit avoir un boss clair</strong> pour éviter les situations où TI
                croit que le métier pilote, le métier croit que TI pilote, et l&apos;audit ne peut rattacher une décision à
                personne.
              </p>
              <p>
                <strong className="text-white">Éviter la double hiérarchie implicite</strong> : si deux parties « possèdent »
                le même agent sans arbitrage écrit, les permissions gonflent et la responsabilité se dilue. Un owner unique ne
                empêche pas la consultation : il force la clarté.
              </p>
              <p>
                <strong className="text-white">Responsabilités explicites avant déploiement</strong> : sans RACI convenu, le
                premier incident révèle des trous — trop tard pour reconstruire proprement la chaîne de décision. Le cadre se
                négocie <em>avant</em> la mise en production, pas après.
              </p>
            </div>
          </div>
        );

      case 'architecture':
        return (
          <div className="space-y-8">
            <section>
              <h3 className="text-lg font-bold text-white mb-4">Schéma de référence — Agentic AI gouverné</h3>
              <p className="text-sm text-slate-300 mb-4 leading-7">
                Aucun agent n&apos;accède directement à l&apos;ensemble du SI. Les flux passent par la couche de gouvernance
                (politiques, consentements, limitations) et par des identités de service distinctes avec journaux corrélés.{' '}
                <strong className="text-white">Le RAG seul ne suffit pas à maîtriser le risque</strong> : une couche{' '}
                <strong className="text-white">firewall RAG</strong> borne explicitement ce qui entre dans les corpus et
                l&apos;index (<strong className="text-rose-200">IN</strong>) et ce qui est autorisé à monter en contexte
                vers le modèle (<strong className="text-rose-200">OUT</strong>), ce qui limite l&apos;errance, le retrieval
                abusif et la dilution du contexte par des sources non approuvées.
              </p>
              <div className="rounded-2xl border border-cyan-400/20 bg-slate-900/70 p-6 overflow-x-auto">
                <div className="flex flex-col gap-4 min-w-[640px]">
                  <div className="flex flex-wrap justify-center gap-3">
                    <div className="rounded-xl border border-slate-500/40 bg-slate-800/80 px-4 py-3 text-center text-sm font-semibold text-white">
                      Utilisateurs
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-500 self-center hidden sm:block" />
                    <div className="rounded-xl border border-indigo-400/40 bg-indigo-500/20 px-4 py-3 text-center text-sm font-semibold text-indigo-100 max-w-xs">
                      Couche de gouvernance
                      <p className="text-[10px] font-normal text-indigo-200/80 mt-1">Politiques, approbations, quotas</p>
                    </div>
                  </div>
                  <p className="text-center text-[11px] text-slate-400 px-2">
                    Le <span className="text-rose-200 font-semibold">firewall RAG</span> est la barrière d&apos;architecture
                    entre ingestion / indexation et exposition au modèle — pas un simple filtre « après coup ».
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 items-stretch">
                    {[
                      { label: 'Registre / identité agents', icon: FileSearch, tone: 'default' as const },
                      { label: 'Modèles IA', icon: Cpu, tone: 'default' as const },
                      {
                        label: 'Firewall RAG',
                        icon: Shield,
                        tone: 'firewall' as const,
                        sub: 'IN : sources, index, politiques · OUT : chunks, quotas, filtrage contexte',
                      },
                      { label: 'Mémoire / RAG gouverné', icon: Database, tone: 'default' as const },
                      { label: 'Outils / connecteurs', icon: GitBranch, tone: 'default' as const },
                    ].map((b) => (
                      <div
                        key={b.label}
                        className={`flex-1 min-w-[118px] max-w-[200px] rounded-xl p-3 flex flex-col items-center gap-2 text-center ${
                          b.tone === 'firewall'
                            ? 'border-2 border-rose-400/55 bg-rose-950/35 shadow-[0_0_24px_-4px_rgba(244,63,94,0.35)]'
                            : 'border border-white/15 bg-white/5'
                        }`}
                      >
                        <b.icon
                          className={`w-6 h-6 shrink-0 ${b.tone === 'firewall' ? 'text-rose-300' : 'text-cyan-300'}`}
                        />
                        <span
                          className={`text-xs font-semibold leading-tight ${b.tone === 'firewall' ? 'text-rose-100' : 'text-slate-100'}`}
                        >
                          {b.label}
                        </span>
                        {'sub' in b && b.sub ? (
                          <span className="text-[10px] font-normal text-rose-200/85 leading-snug">{b.sub}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap justify-center gap-3 items-center">
                    <div className="rounded-xl border border-slate-500/40 bg-slate-800/80 px-4 py-3 text-sm font-semibold text-white">
                      Systèmes d&apos;entreprise
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-xs text-emerald-100">
                      <p className="font-bold text-emerald-200 mb-1">Journalisation / audit</p>
                      Corrélation requêtes, outils, identités ; preuves pour revue et enquête.
                    </div>
                    <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                      <p className="font-bold text-amber-200 mb-1">Supervision humaine</p>
                      Files d&apos;escalade, reprises, validation des exceptions.
                    </div>
                    <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-xs text-rose-100">
                      <p className="font-bold text-rose-200 mb-1">Contrôles de sécurité</p>
                      Segmentation, DLP, secrets, détection d&apos;abus et garde-fous techniques.
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-violet-400/20 bg-violet-500/10 p-5">
                <h4 className="font-bold text-violet-100 mb-3 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  RAG sécurisé
                </h4>
                <ul className="space-y-2 text-sm text-slate-200 leading-6">
                  {[
                    'Firewall RAG — IN : validation des sources à l’ingestion, politiques par corpus, refus par défaut des jeux non classifiés ; résistance aux jeux empoisonnés ou non autorisés.',
                    'Firewall RAG — OUT : filtrage et scoring des chunks remontés, plafonds de contexte, allowlists de passages, blocage des extractions hors périmètre avant injection dans le prompt.',
                    'Le firewall matérialise l’architecture « borne IN / borne OUT » : même si le retrieveur « trouve » un document, seuls les fragments conformes franchissent la barrière vers le modèle.',
                    'Indexation sélective : seuls les corpus explicitement approuvés alimentent le retrieval.',
                    'Classification des données appliquée avant exposition au modèle.',
                    'Anonymisation / pseudonymisation lorsque le risque residual l’exige.',
                    'Segmentation des corpus (pas de mélange ad hoc de sensibilités).',
                    'Contrôle des sources : fraîcheur, authenticité, droits.',
                    'Limitation du contexte : fenêtres, filtres, budgets de tokens pour réduire l’exfiltration implicite.',
                    'Secrets et clés hors portée du modèle ; rotation et coffres conformes.',
                    'Traçabilité des requêtes sensibles pour revue ciblée.',
                  ].map((x) => (
                    <li key={x} className="flex gap-2">
                      <span className="text-violet-300 shrink-0">•</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-sky-400/20 bg-sky-500/10 p-5">
                <h4 className="font-bold text-sky-100 mb-3 flex items-center gap-2">
                  <GitBranch className="w-5 h-5" />
                  Orchestration sécurisée
                </h4>
                <ul className="space-y-2 text-sm text-slate-200 leading-6">
                  {[
                    'Hiérarchie claire : orchestrateur parent et agents enfants à périmètres contractuels.',
                    'Rôles spécialisés : pas de « tout faire » — chaque sous-agent a un mandat étroit.',
                    'Périmètres explicites pour éviter le chevauchement flou des responsabilités.',
                    'Escalade : file et critères lorsque seuils, incertitudes ou conflits détectés.',
                    'Arrêt / kill switch : coupure centralisée, mode dégradé et reprise documentée.',
                    'Journaux agrégés par corrélation d’ID de flux pour reconstituer une chaîne complète.',
                  ].map((x) => (
                    <li key={x} className="flex gap-2">
                      <span className="text-sky-300 shrink-0">•</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>
        );

      case 'rituels':
        return (
          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-bold text-white mb-4">Rituels de gouvernance</h3>
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-left text-xs md:text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-slate-900/90">
                      <th className="p-3 font-bold text-indigo-200">Rituel</th>
                      <th className="p-3 font-bold text-indigo-200">Objectif</th>
                      <th className="p-3 font-bold text-indigo-200">Fréquence</th>
                      <th className="p-3 font-bold text-indigo-200">Participants</th>
                      <th className="p-3 font-bold text-indigo-200">Livrables</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-200">
                    {[
                      {
                        n: 'Revue mensuelle des agents',
                        o: 'Détecter dérive, obsolescence, permissions élargies et valeur réelle.',
                        f: 'Mensuelle',
                        p: 'Owners, gouvernance, plateforme TI',
                        l: 'Registre à jour, actions correctives, décisions d’autonomie',
                      },
                      {
                        n: 'Revue trimestrielle des accès',
                        o: 'Réduire la dette de privilèges et valider le moindre accès effectif.',
                        f: 'Trimestrielle',
                        p: 'Sécurité, TI, owners, données',
                        l: 'Rapport d’écart, plan de réduction, attestations',
                      },
                      {
                        n: 'Revue trimestrielle RAG / sources',
                        o: 'Garantir classification, qualité et légitimité des corpus.',
                        f: 'Trimestrielle',
                        p: 'Données, sécurité, arch. IA',
                        l: 'Inventaire des sources, changements, risques résiduels',
                      },
                      {
                        n: 'Revue incidents & anomalies',
                        o: 'Comprendre causes systémiques et renforcer garde-fous.',
                        f: 'Après incident + rythme minimal trimestriel',
                        p: 'Sécurité, TI, owner, audit',
                        l: 'Chronologie, leçons, mises à jour de politique',
                      },
                      {
                        n: "Revue d'évolution d'autonomie",
                        o: 'Élargir ou restreindre les niveaux sur preuves, pas sur pression.',
                        f: 'Semestrielle ou avant changement majeur',
                        p: 'Sponsor, gouvernance, sécurité',
                        l: 'Décision écrite, mise à jour RACI et registre',
                      },
                      {
                        n: 'Revue performance & valeur',
                        o: 'Aligner coûts, risques et bénéfices métier ; couper ce qui ne rend pas.',
                        f: 'Trimestrielle',
                        p: 'Sponsor, métier, finance si pertinent',
                        l: 'Tableau de bord, décisions de continuation / retrait',
                      },
                    ].map((r) => (
                      <tr key={r.n} className="border-b border-white/5 align-top">
                        <td className="p-3 font-semibold text-white">{r.n}</td>
                        <td className="p-3">{r.o}</td>
                        <td className="p-3 whitespace-nowrap">{r.f}</td>
                        <td className="p-3">{r.p}</td>
                        <td className="p-3">{r.l}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-bold text-white mb-3">Indicateurs de gouvernance (KPI / KRI)</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  '% d’agents avec owner nommé et à jour',
                  '% d’agents avec niveau d’autonomie déclaré et approuvé',
                  '% d’agents avec permissions revues dans les 90 jours',
                  '% d’agents avec journalisation active et corrélation d’ID de flux',
                  'Nombre d’incidents / anomalies (et gravité) par trimestre',
                  '% de sources RAG classifiées et approuvées',
                  'Délai moyen de clôture de revue post-incident',
                ].map((k) => (
                  <div key={k} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-200 flex gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-300 shrink-0 mt-0.5" />
                    <span>{k}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-5">
              <h3 className="text-lg font-bold text-emerald-100 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Conditions minimales avant mise en production
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-100">
                {[
                  'Owner nommé et disponible pour décisions opérationnelles',
                  "Cas d'usage approuvé par le sponsor (périmètre, données, valeur attendue)",
                  "Niveau d'autonomie déclaré et validé",
                  'Permissions validées (moindre privilège, durée / périmètre)',
                  'Sources de données et corpus RAG approuvés',
                  'Journalisation active et consultable par la gouvernance / sécurité',
                  "Mécanisme d'escalade défini (contacts, SLA, file de reprise)",
                  "Mécanisme d'arrêt défini (kill switch, mode dégradé, communication interne)",
                ].map((c) => (
                  <li key={c} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-300 mt-0.5" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        );

      default:
        return null;
    }
  }, [activeTab]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="relative z-10 w-[96vw] max-w-7xl h-[92vh] overflow-hidden rounded-4xl border border-indigo-400/20 bg-slate-950 shadow-2xl flex flex-col"
        >
          <div className="relative overflow-hidden bg-linear-to-r from-slate-900 via-indigo-950 to-slate-900 px-6 md:px-8 py-6 md:py-7 text-white border-b border-indigo-500/20 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 md:right-6 md:top-6 rounded-xl bg-white/10 p-2 hover:bg-white/20 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="pr-12">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-4xl font-black tracking-tight">Gouvernance Agentic AI</h1>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300/40 bg-indigo-500/20 px-3 py-1 text-[10px] md:text-xs font-semibold text-indigo-100">
                  <Shield className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  Cadre PRIsME — gouvernance / sécurité / confiance
                </span>
              </div>
              <p className="text-sm md:text-base text-white/85 max-w-5xl leading-7">
                Cadre stratégique pour déployer des agents IA capables d&apos;agir :{' '}
                <strong className="text-white">identité</strong>,{' '}
                <strong className="text-white">permissions</strong>,{' '}
                <strong className="text-white">données & RAG</strong>,{' '}
                <strong className="text-white">orchestration</strong> et{' '}
                <strong className="text-white">supervision humaine</strong> — sans confondre avec l&apos;exécution
                opérationnelle (TactiCE).
              </p>
            </div>
          </div>

          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex flex-wrap gap-2 px-4 md:px-6 py-3 border-b border-white/10 bg-slate-950/95 shrink-0 overflow-x-auto">
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${
                      active
                        ? 'bg-indigo-500/30 text-white border border-indigo-400/40'
                        : 'bg-white/5 text-slate-300 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0 opacity-90" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">{tabContent}</div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
